import React, { Suspense, lazy, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

// Lazy-imported so the dock's bundle (incl. WS handlers, emoji picker, message
// bubbles) doesn't block the initial paint of every authenticated route.
const DesktopChatDock = lazy(() =>
  import('@/components/DesktopChatDock').then((m) => ({ default: m.DesktopChatDock }))
);

/**
 * DesktopChatDockLoader — render gate for the floating desktop chat widget.
 *
 * Defers the actual JS chunk fetch until:
 *   1. The user is authenticated.
 *   2. We aren't on /messages (the full Messages page already shows the UI).
 *   3. The viewport is at md+ (the dock is hidden on mobile by CSS anyway, but
 *      we skip the fetch entirely so phones don't pay for code they never run).
 *   4. The browser is idle (`requestIdleCallback`, falling back to a 300ms
 *      timeout). This keeps the dock off the critical path for first paint.
 *
 * The actual DesktopChatDock component (and its periodic /notifications
 * polling, WS subscription, etc.) stays unchanged — we just delay mounting it.
 */
export function DesktopChatDockLoader() {
  const { user } = useAuth();
  const location = useLocation();
  const onMessages = location.pathname.startsWith('/messages');
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    if (!user || onMessages) {
      setShouldLoad(false);
      return undefined;
    }
    if (typeof window === 'undefined') return undefined;
    // Skip on mobile — the dock has `hidden md:block` styling and never paints
    // there, so loading the chunk would be pure waste.
    try {
      if (window.matchMedia && window.matchMedia('(max-width: 767px)').matches) {
        return undefined;
      }
    } catch {
      /* matchMedia not available — fall through */
    }

    const ric = window.requestIdleCallback;
    const cic = window.cancelIdleCallback;
    let handle;
    let timer;
    if (typeof ric === 'function') {
      handle = ric(() => setShouldLoad(true), { timeout: 2000 });
    } else {
      timer = setTimeout(() => setShouldLoad(true), 300);
    }
    return () => {
      if (handle != null && typeof cic === 'function') cic(handle);
      if (timer != null) clearTimeout(timer);
    };
  }, [user, onMessages]);

  if (!user || onMessages || !shouldLoad) return null;
  return (
    <Suspense fallback={null}>
      <DesktopChatDock />
    </Suspense>
  );
}

export default DesktopChatDockLoader;
